import { useState, useEffect, useRef, type FormEvent, type ReactNode } from 'react';
import { X, Package, AlertCircle } from 'lucide-react';
import { createItem, updateItem } from '../utils/itemApi';
import { deleteItemImage, getItemImage, uploadItemImage } from '../utils/itemAttachmentApi';
import { getAllAccounts } from '../utils/accountApi';
import { getAllItemCategories, createItemCategory } from '../utils/itemCategoryApi';
import { ApiValidationError } from '../types/auth';
import type { Attachment } from '../types/attachment';
import type { Item, ItemType, UnitOfMeasure } from '../types/item';
import type { GlAccount } from '../types/account';
import type { ItemCategory } from '../types/itemCategory';
import { getDefaultGlAccountsForItemType } from '../utils/itemGlDefaults';
import { formatQuantity } from '../utils/formatCurrency';
import {
  getAllowNegativeInventory,
  hasOpenBackorders,
  parseInventoryQty,
  qtyOnHandClassName,
} from '../utils/negativeInventory';
import ItemImageField from './ItemImageField';
import { validateItemImageFile } from '../utils/itemImageValidation';
import {
  canDeleteItem,
  canEditItem,
  defaultItemFormData,
  formatItemLabel,
  itemToFormData,
  ITEM_TYPES,
  UNITS_OF_MEASURE,
  toCreateItemPayload,
  validateItemForm,
  type ItemFormData,
} from '../utils/itemForm';

const FormSection = ({
  title,
  description,
  children,
}: {
  title: ReactNode;
  description?: string;
  children: ReactNode;
}) => (
  <section className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
    <div className="border-b border-gray-100 pb-3">
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      {description && <p className="mt-1 text-xs text-gray-500 leading-relaxed">{description}</p>}
    </div>
    {children}
  </section>
);

export interface ItemSavedMeta {
  /** Item fields saved but image step failed — keep modal open */
  imageError?: string;
}

export interface ItemFormModalProps {
  item?: Item | null;
  onClose: () => void;
  onSaved: (item: Item, meta?: ItemSavedMeta) => void;
  onDeleteRequest?: (item: Item) => void;
  initialName?: string;
  defaultItemType?: ItemType;
  subtitle?: string;
}

const ItemFormModal = ({
  item,
  onClose,
  onSaved,
  onDeleteRequest,
  initialName = '',
  defaultItemType = 'INVENTORY',
  subtitle,
}: ItemFormModalProps) => {
  // Tracks an item that was created successfully but whose image step failed.
  // Using this ref avoids a race condition where the parent hasn't yet updated
  // the `item` prop (it awaits fetchItems first), so a second Save attempt
  // would otherwise call createItem again instead of updateItem.
  const partialSavedItemRef = useRef<Item | null>(null);
  const isCreateSessionRef = useRef(!item);

  // The "effective" item for this form session: the externally supplied item
  // OR one we already created in this session (even if image failed).
  const effectiveItem = partialSavedItemRef.current ?? item ?? null;
  const isEdit = Boolean(effectiveItem);

  const [formData, setFormData] = useState<ItemFormData>(() =>
    item
      ? itemToFormData(item)
      : defaultItemFormData({ name: initialName, itemType: defaultItemType })
  );
  const [accounts, setAccounts] = useState<GlAccount[]>([]);
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalError, setModalError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[] | undefined>>({});
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryParentId, setNewCategoryParentId] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [existingImage, setExistingImage] = useState<Attachment | null>(null);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [submitPhase, setSubmitPhase] = useState<'idle' | 'saving' | 'uploading' | 'removing'>('idle');

  const submitting = submitPhase !== 'idle';

  // Depend on item?.id (stable primitive) rather than the item object reference.
  // This prevents the effect from re-running — and wiping pendingImageFile — when
  // the parent passes a new object reference for the same item (e.g. after an image
  // upload failure, Products.tsx calls setItemFormTarget(savedItem) which changes
  // the object reference even though the item ID hasn't changed).
  const itemId = item?.id;
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setExistingImage(null);
        setPendingImageFile(null);
        setRemoveExistingImage(false);
        setImageError(null);

        const [loadedAccounts, loadedCategories, loadedImage] = await Promise.all([
          canEditItem() ? getAllAccounts().catch(() => [] as GlAccount[]) : Promise.resolve([]),
          getAllItemCategories({ active: true }).catch(() => [] as ItemCategory[]),
          itemId
            ? getItemImage(itemId).catch((err) => {
                console.error('[ItemFormModal] Failed to load item image', err);
                return null;
              })
            : Promise.resolve(null),
        ]);
        if (cancelled) return;

        setAccounts(loadedAccounts);
        setCategories(loadedCategories);
        setExistingImage(loadedImage);

        if (!itemId) {
          const base = defaultItemFormData({ name: initialName, itemType: defaultItemType });
          const glDefaults = getDefaultGlAccountsForItemType(base.item_type, loadedAccounts);
          setFormData({ ...base, ...glDefaults });
          if (loadedCategories.length === 0 && canEditItem()) {
            setShowCreateCategory(true);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setModalError(err instanceof Error ? err.message : 'Failed to load form data');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [itemId, initialName, defaultItemType]);

  const sortedCategories = [...categories]
    .filter((c) => c.active)
    .sort((a, b) => a.fully_qualified_name.localeCompare(b.fully_qualified_name));

  const parentCategoryOptions = sortedCategories.filter((c) => c.level < 3);
  const incomeAccounts = accounts.filter((a) => a.account_type === 'INCOME' && a.active);
  const expenseAccounts = accounts.filter((a) => a.account_type === 'EXPENSE' && a.active);
  const assetAccounts = accounts.filter((a) => a.account_type === 'ASSET' && a.active);

  const fieldError = (field: string) => fieldErrors[field]?.[0];

  const resetCreateCategory = () => {
    setShowCreateCategory(false);
    setNewCategoryName('');
    setNewCategoryParentId('');
    setCategoryError(null);
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) {
      setCategoryError('Category name is required.');
      return;
    }

    setCategoryError(null);
    setCreatingCategory(true);

    try {
      const created = await createItemCategory({
        name,
        parent_category_id: newCategoryParentId ? Number(newCategoryParentId) : null,
        active: true,
      });
      setCategories((prev) =>
        [...prev, created].sort((a, b) => a.fully_qualified_name.localeCompare(b.fully_qualified_name))
      );
      setFormData((prev) => ({ ...prev, category_id: String(created.id) }));
      resetCreateCategory();
    } catch (err) {
      if (err instanceof ApiValidationError) {
        const nameErr = err.errors.name?.[0];
        const parentErr = err.errors.parent_category_id?.[0];
        setCategoryError(nameErr || parentErr || err.message);
      } else {
        setCategoryError(err instanceof Error ? err.message : 'Failed to create category');
      }
    } finally {
      setCreatingCategory(false);
    }
  };

  const applySuggestedGlAccounts = () => {
    const defaults = getDefaultGlAccountsForItemType(formData.item_type, accounts);
    setFormData((prev) => ({ ...prev, ...defaults }));
  };

  const handleItemTypeChange = (itemType: ItemType) => {
    setFormData((prev) => {
      const next = { ...prev, item_type: itemType };
      if (!isEdit) {
        return { ...next, ...getDefaultGlAccountsForItemType(itemType, accounts) };
      }
      return next;
    });
  };

  const renderAccountSelect = (
    label: string,
    value: string,
    onChange: (val: string) => void,
    options: GlAccount[],
    hint?: string
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
      >
        <option value="">None</option>
        {options.map((account) => (
          <option key={account.id} value={account.id}>
            {account.account_number} — {account.name}
          </option>
        ))}
      </select>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setModalError(null);
    setFieldErrors({});
    setImageError(null);

    const validationErrors = validateItemForm(formData);
    if (validationErrors) {
      setFieldErrors(validationErrors);
      setModalError('Please fix the highlighted fields.');
      return;
    }

    if (pendingImageFile) {
      const imageValidation = validateItemImageFile(pendingImageFile);
      if (!imageValidation.valid) {
        setImageError(imageValidation.message);
        return;
      }
    }

    const hasImage =
      Boolean(pendingImageFile) || Boolean(existingImage && !removeExistingImage);

    if (isCreateSessionRef.current && !hasImage) {
      if (!canEditItem()) {
        setImageError('Your role cannot upload item images. Ask an admin or accountant.');
      } else {
        setImageError('Product image is required.');
      }
      return;
    }

    if (pendingImageFile && !canEditItem()) {
      setImageError('Your role cannot upload item images. Ask an admin or accountant.');
      return;
    }

    const needsImageDelete = canEditItem() && removeExistingImage && !pendingImageFile;
    const needsImageUpload = canEditItem() && Boolean(pendingImageFile);

    let savedItem: Item | null = null;

    try {
      setSubmitPhase('saving');
      const payload = toCreateItemPayload(formData);
      // Use effectiveItem (which includes any item we already created in this
      // session whose image upload failed) so retries never call createItem twice.
      savedItem = effectiveItem ? await updateItem(effectiveItem.id, payload) : await createItem(payload);

      if (needsImageDelete) {
        setSubmitPhase('removing');
        await deleteItemImage(savedItem.id);
      }

      if (needsImageUpload && pendingImageFile) {
        setSubmitPhase('uploading');
        await uploadItemImage(savedItem.id, pendingImageFile);
      }

      onSaved(savedItem);
      onClose();
    } catch (err) {
      if (!savedItem) {
        if (err instanceof ApiValidationError) {
          setFieldErrors(err.errors);
          setModalError(err.message);
        } else {
          setModalError(err instanceof Error ? err.message : 'Failed to save item');
        }
      } else {
        // Item data saved but image step failed. Store the saved item so that any
        // retry in this same modal session uses updateItem instead of createItem.
        partialSavedItemRef.current = savedItem;
        const imageAction = needsImageUpload ? 'upload' : 'remove';
        const imageErrorMessage =
          err instanceof Error ? err.message : 'Please try again.';
        const message = `Item saved, but image ${imageAction} failed. ${imageErrorMessage}`;
        console.error('[ItemFormModal] Image step failed after item save', err);
        setModalError(message);
        setImageError(imageErrorMessage);
        onSaved(savedItem, { imageError: message });
      }
    } finally {
      setSubmitPhase('idle');
    }
  };

  const submitLabel =
    submitPhase === 'saving'
      ? 'Saving...'
      : submitPhase === 'uploading'
        ? 'Uploading image...'
        : submitPhase === 'removing'
          ? 'Removing image...'
          : isEdit
            ? 'Save Changes'
            : 'Create Item';

  return (
    <div className="fixed inset-0 bg-gray-800/75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <Package className="h-6 w-6 text-slate-700" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isEdit ? 'Edit Item' : 'Create Item'}
              </h3>
              {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
            <div className="p-6 space-y-5 overflow-y-auto flex-1">
              {modalError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{modalError}</span>
                </div>
              )}

              <FormSection
                title="Basic information"
                description="Core identifiers and catalog notes."
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SKU <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      maxLength={80}
                      value={formData.sku}
                      onChange={(e) => setFormData((prev) => ({ ...prev, sku: e.target.value }))}
                      className={`w-full border rounded-lg px-3 py-2 text-sm font-mono ${
                        fieldError('sku') ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="WDG-001"
                    />
                    {fieldError('sku') && (
                      <p className="mt-1 text-sm text-red-600">{fieldError('sku')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      maxLength={250}
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      className={`w-full border rounded-lg px-3 py-2 text-sm ${
                        fieldError('name') ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Test Widget"
                    />
                    {fieldError('name') && (
                      <p className="mt-1 text-sm text-red-600">{fieldError('name')}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={3}
                    required
                    maxLength={5000}
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, description: e.target.value }))
                    }
                    className={`w-full border rounded-lg px-3 py-2 text-sm ${
                      fieldError('description') ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Heavy-duty widget for industrial use."
                  />
                  {fieldError('description') && (
                    <p className="mt-1 text-sm text-red-600">{fieldError('description')}</p>
                  )}
                </div>
              </FormSection>

              <FormSection
                title={
                  <>
                    Catalog image
                    {isCreateSessionRef.current && <span className="text-red-500"> *</span>}
                  </>
                }
                description={
                  isCreateSessionRef.current
                    ? 'Upload a product image for your catalog.'
                    : 'Optional product image for your catalog.'
                }
              >
                <ItemImageField
                  existingImage={existingImage}
                  pendingFile={pendingImageFile}
                  removeExisting={removeExistingImage}
                  required={isCreateSessionRef.current}
                  onFileSelect={(file) => {
                    setPendingImageFile(file);
                    setRemoveExistingImage(false);
                    setImageError(null);
                  }}
                  onRemoveExisting={() => setRemoveExistingImage(true)}
                  disabled={submitting}
                  readOnly={!canEditItem()}
                  error={imageError}
                />
              </FormSection>

              <FormSection
                title="Cost"
                description="Reference purchase cost for this item. Informational only — not used for FIFO or COGS."
              >
                <div className="max-w-xs">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Standard cost</label>
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={formData.standard_cost}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, standard_cost: e.target.value }))
                    }
                    className={`w-full border rounded-lg px-3 py-2 text-sm ${
                      fieldError('standard_cost') ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {fieldError('standard_cost') && (
                    <p className="mt-1 text-sm text-red-600">{fieldError('standard_cost')}</p>
                  )}
                </div>
              </FormSection>

              <FormSection
                title="Selling prices"
                description="Reference sell prices for this item. Dealer and cash prices are informational only. Regular selling price is the default on new invoice lines."
              >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dealer price</label>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={formData.dealer_selling_price}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, dealer_selling_price: e.target.value }))
                      }
                      className={`w-full border rounded-lg px-3 py-2 text-sm ${
                        fieldError('dealer_selling_price') ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cash price</label>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={formData.cash_price}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, cash_price: e.target.value }))
                      }
                      className={`w-full border rounded-lg px-3 py-2 text-sm ${
                        fieldError('cash_price') ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Regular selling price <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      min={0}
                      step="any"
                      value={formData.unit_price}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, unit_price: e.target.value }))
                      }
                      className={`w-full border rounded-lg px-3 py-2 text-sm ${
                        fieldError('unit_price') ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {fieldError('unit_price') && (
                      <p className="mt-1 text-sm text-red-600">{fieldError('unit_price')}</p>
                    )}
                  </div>
                </div>
              </FormSection>

              <FormSection
                title="Classification"
                description="Group the item in your catalog and define how it is sold or tracked."
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.category_id}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, category_id: e.target.value }))
                    }
                    className={`w-full border rounded-lg px-3 py-2 text-sm bg-white ${
                      fieldError('category_id') ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="" disabled>
                      Select a category…
                    </option>
                    {sortedCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.fully_qualified_name}
                      </option>
                    ))}
                  </select>
                  {fieldError('category_id') && (
                    <p className="mt-1 text-sm text-red-600">{fieldError('category_id')}</p>
                  )}
                  {canEditItem() && (
                    <div className="mt-2">
                      {!showCreateCategory ? (
                        <button
                          type="button"
                          onClick={() => setShowCreateCategory(true)}
                          className="text-sm text-slate-700 hover:text-slate-900 underline"
                        >
                          + Create new category
                        </button>
                      ) : (
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                            New category
                          </p>
                          {categoryError && (
                            <p className="text-sm text-red-600 flex items-start gap-1.5">
                              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                              {categoryError}
                            </p>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              maxLength={100}
                              value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                              placeholder="e.g. Hardware"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Parent category
                            </label>
                            <select
                              value={newCategoryParentId}
                              onChange={(e) => setNewCategoryParentId(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                            >
                              <option value="">Top level (no parent)</option>
                              {parentCategoryOptions.map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.fully_qualified_name}
                                </option>
                              ))}
                            </select>
                            <p className="mt-1 text-xs text-gray-500">
                              Categories support up to 3 levels deep.
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleCreateCategory}
                              disabled={creatingCategory}
                              className="px-3 py-1.5 bg-slate-800 text-white rounded-md text-sm disabled:opacity-60"
                            >
                              {creatingCategory ? 'Creating...' : 'Create & select'}
                            </button>
                            <button
                              type="button"
                              onClick={resetCreateCategory}
                              disabled={creatingCategory}
                              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {sortedCategories.length === 0 && !showCreateCategory && (
                    <p className="mt-1 text-xs text-gray-500">
                      No categories yet — create one to organize this item.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label>
                    <select
                      value={formData.item_type}
                      onChange={(e) => handleItemTypeChange(e.target.value as ItemType)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                    >
                      {ITEM_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {formatItemLabel(type)}
                        </option>
                      ))}
                    </select>
                    {!isEdit && (
                      <p className="mt-1 text-xs text-gray-500">
                        Changing type updates suggested GL accounts for new items.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit of Measure</label>
                    <select
                      value={formData.unit_of_measure}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          unit_of_measure: e.target.value as UnitOfMeasure,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                    >
                      {UNITS_OF_MEASURE.map((unit) => (
                        <option key={unit} value={unit}>
                          {formatItemLabel(unit)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </FormSection>

              <FormSection
                title="Inventory & status"
                description="Stock thresholds and whether this item is active or taxable."
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Point</label>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={formData.reorder_point}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, reorder_point: e.target.value }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>

                  {item && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Qty on Hand</label>
                      <input
                        type="text"
                        readOnly
                        value={formatQuantity(item.qty_on_hand)}
                        className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 ${qtyOnHandClassName(item.qty_on_hand)}`}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        System-managed — updated by bills and invoices
                      </p>
                    </div>
                  )}
                  {item && getAllowNegativeInventory() && item.item_type === 'INVENTORY' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Backorder Qty
                      </label>
                      <input
                        type="text"
                        readOnly
                        value={
                          parseInventoryQty(item.backorder_qty) > 0
                            ? formatQuantity(item.backorder_qty ?? '0')
                            : '—'
                        }
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-600"
                      />
                      {hasOpenBackorders(item) && (
                        <p className="mt-1 text-xs text-rose-700">
                          Open backorders block item deletion until cleared.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.taxable}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, taxable: e.target.checked }))
                      }
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Taxable
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, active: e.target.checked }))
                      }
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Active
                  </label>
                </div>
              </FormSection>

              {canEditItem() && accounts.length > 0 && (
                <FormSection
                  title="GL accounts"
                  description="Link ledger accounts used when this item is sold, purchased, or stocked. On new items, accounts are auto-selected from your chart of accounts by sub-type."
                >
                  <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-xs text-blue-900 space-y-2">
                    <p className="font-medium">How auto-selection works</p>
                    <ul className="list-disc pl-4 space-y-1 text-blue-800">
                      <li>
                        <strong>Income</strong> → first active account with sub-type{' '}
                        <code className="font-mono">SALES_REVENUE</code>
                      </li>
                      <li>
                        <strong>Inventory asset</strong> →{' '}
                        <code className="font-mono">INVENTORY_ASSET</code> (inventory items only)
                      </li>
                      <li>
                        <strong>Expense / COGS</strong> → <code className="font-mono">COGS</code>{' '}
                        (inventory &amp; non-inventory)
                      </li>
                    </ul>
                    <button
                      type="button"
                      onClick={applySuggestedGlAccounts}
                      className="text-blue-900 underline hover:text-blue-950"
                    >
                      Apply suggested accounts now
                    </button>
                  </div>

                  {renderAccountSelect(
                    'Income Account',
                    formData.income_account_id,
                    (val) => setFormData((prev) => ({ ...prev, income_account_id: val })),
                    incomeAccounts,
                    'Sales revenue account (SALES_REVENUE)'
                  )}
                  {(formData.item_type === 'INVENTORY' || formData.item_type === 'NON_INVENTORY') &&
                    renderAccountSelect(
                      formData.item_type === 'INVENTORY' ? 'Asset Account' : 'Expense Account',
                      formData.item_type === 'INVENTORY'
                        ? formData.asset_account_id
                        : formData.expense_account_id,
                      (val) =>
                        setFormData((prev) =>
                          formData.item_type === 'INVENTORY'
                            ? { ...prev, asset_account_id: val }
                            : { ...prev, expense_account_id: val }
                        ),
                      formData.item_type === 'INVENTORY' ? assetAccounts : expenseAccounts,
                      formData.item_type === 'INVENTORY'
                        ? 'Inventory asset account (INVENTORY_ASSET)'
                        : 'Expense / COGS account (COGS)'
                    )}
                  {formData.item_type === 'INVENTORY' &&
                    renderAccountSelect(
                      'Expense Account (optional)',
                      formData.expense_account_id,
                      (val) => setFormData((prev) => ({ ...prev, expense_account_id: val })),
                      expenseAccounts,
                      'COGS account — optional at create'
                    )}
                </FormSection>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3 shrink-0">
              {item && canDeleteItem() && onDeleteRequest && (
                <button
                  type="button"
                  onClick={() => onDeleteRequest(item)}
                  className="mr-auto px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700"
                  disabled={submitting}
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-slate-800 text-white rounded-md text-sm disabled:opacity-60"
              >
                {submitLabel}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ItemFormModal;
